/**
 * @fileoverview `<PageHeader>` — en-tête standard des pages métier GNPC.
 *
 * Les pages backoffice doivent ouvrir par un bloc stable : libellé de domaine,
 * titre, description opérationnelle et actions éventuelles. Centraliser ce
 * patron évite les variations de spacing entre Dashboard, Paiements, Audit,
 * Utilisateurs et Exports.
 */

import type { ReactNode } from 'react';
import { cn } from '../lib/utils.ts';

export interface PageHeaderProps {
  /** Libellé court affiché au-dessus du titre, ex : `BO-04 · Paiements`. */
  eyebrow?: ReactNode;
  /** Titre principal de la page. */
  title: ReactNode;
  /** Description métier concise, idéalement une à deux lignes. */
  description?: ReactNode;
  /** Zone d'actions alignée à droite sur desktop. */
  actions?: ReactNode;
  /** Métadonnée secondaire sous la description : date, périmètre, rôle. */
  meta?: ReactNode;
  /** Classe CSS complémentaire du conteneur racine. */
  className?: string;
}

/**
 * En-tête de page métier.
 *
 * @param props - Contenu textuel, actions et classe CSS.
 * @returns Bloc responsive sans carte imbriquée ni ombre.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   eyebrow="BO-04"
 *   title="Paiements"
 *   description="Suivre les paiements et les enregistrements STI."
 *   actions={<Button>Exporter</Button>}
 * />
 * ```
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  meta,
  className,
}: PageHeaderProps): React.JSX.Element {
  return (
    <header className={cn('flex flex-col gap-4 md:flex-row md:items-start md:justify-between', className)}>
      <div className="min-w-0 space-y-2">
        {eyebrow ? <p className="text-quipux-orange-600 text-sm font-medium">{eyebrow}</p> : null}
        <h1 className="text-quipux-navy-950 text-2xl font-semibold tracking-tight md:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="text-muted-foreground max-w-3xl text-sm leading-6">{description}</p>
        ) : null}
        {meta ? <div className="text-muted-foreground text-xs">{meta}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
